import React, {createContext, useCallback, useEffect, useMemo, useRef, useState} from 'react'
import InfoCube from '../widgets/InfoCube';
import MiniTag from './MiniTag';
import useWebSocketComm from '../../hooks/useWebSocketComm';
import useKeyboard from '../../hooks/useKeyboard';
import ProgPanel from './ProgPanel';
import useDisplayTime from '../../hooks/useDisplayTime';
import { useOperatorContext } from '../login/OperatorContextProvider';
import './Tikia.css'
import useWebSocketCaller from '../../hooks/useWebSocketCaller';

export const TikiaContext = createContext();

const bgTodoNextRelease = '#150316';
const bgTodoLater       = '#4a0d50';
const bgTodoNow         = '#600668';
const bgResearch        = '#311a07';
const bgNewBranch       = '#6e3e16';
const bgDesign          = '#b46d32';
const bgImplHighLevel   = '#5f1205';
const bgImplCore        = '#992f1d';
const bgImplEdges       = '#e26953';
const bgHappyFlow       = '#052908';
const bgFullyTested     = '#007c0a';
const bgReview          = '#106359';
const bgMerged          = '#56ddb2';
const bgStagingVerified = '#0082ad';
const bgDeployment      = '#1966b4';
const bgProduction      = '#0080ff';

const stages = {
  todoNextRelease:{ c: bgTodoNextRelease, t: 'Next Release',     p:0 },
  todoLater:      { c: bgTodoLater,       t: 'Later',            p:3 },
  todoNow:        { c: bgTodoNow,         t: 'Now',              p:5 },
  research:       { c: bgResearch,        t: 'Research',         p:15 },
  newBranch:      { c: bgNewBranch,       t: 'New Branch',       p:18 },
  design:         { c: bgDesign,          t: 'Design',           p:25 },
  codeHighLevel:  { c: bgImplHighLevel,   t: 'Dev High Level',   p:33 },
  codeCore:       { c: bgImplCore,        t: 'Dev Core' ,        p:50 },
  codeEdges:      { c: bgImplEdges,       t: 'Dev Edges' ,       p:66 },
  happyFlow:      { c: bgHappyFlow,       t: 'Test HappyFlow' ,  p:70 },
  fullyTested:    { c: bgFullyTested,     t: 'Test Full' ,       p:80 },
  review:         { c: bgReview,          t: 'Commit Review' ,   p:85 },
  merged:         { c: bgMerged,          t: 'Merge' ,           p:90 },
  stagingOK:      { c: bgStagingVerified, t: 'Staging' ,         p:95 },
  deploymentOK:   { c: bgDeployment,      t: 'Deployment' ,      p:98 },
  production:     { c: bgProduction,      t: 'Production' ,      p:100}
}

const sortedStageKeys = Object.keys(stages).sort((a,b)=> stages[a].p - stages[b].p);

//
// -------  TIKIA  --------
//
export default function Tikia({tikiName, tasks, whenTaskUpdated}) {
  const {sendMessage} = useWebSocketCaller();
  const totalTasksRef = useRef(tasks.length);
  console.log('[Tikia] Enter [tikiName: %s] [tasks: %s]', tikiName, tasks.length);
  
  // ---[  Sort By Stage ]---
  //
  const sortByStage = useCallback((tasksArr) => {
    if (stages) {
      return [...tasksArr].sort((a, b) => {
        const A = a.stageId ? stages[a.stageId].p : stages['todoLater'].p;
        const B = b.stageId ? stages[b.stageId].p : stages['todoLater'].p;
        return B - A;
      });
    }
    else return tasksArr;
  }, []);
  
  const {operator}                    = useOperatorContext();  
  const [cubeReset, setCubeReset]     = useState(false);
  const [keyPressed, setKeyPressed]   = useState(null);
  const [tikiTasks, setTikiTasks]     = useState([]);
  const [maxDisplay, setMaxDisplay]   = useState(18);

  const getTotalTasks = ()=>{
    return totalTasksRef.current;
  };

  // ---[  KeyDown  ]---
  //
  const myKeyDown = useCallback((key) => {
    console.log('myKeyDown: %s', key);
    setKeyPressed(key);
    
    if (key === 'Escape') {
      setCubeReset(prev => !prev);
    }
    else if (key === 'P') {
      setTikiTasks(prev => sortByStage([...prev]));
    }
    else if (key === '+') {
      const top = getTotalTasks();
      setMaxDisplay(prev => prev < top ? prev + 1 : top);
    }
    else if (key === '-') {
      setMaxDisplay(prev => prev > 1 ? prev - 1 : 1);
    }
    else if (key === 'ArrowRight') {
      // trigger key right pressed... etc
    }
  }, [sortByStage]);

  useKeyboard(myKeyDown);

  //
  // ---[  useWebSocketComm  ]---
  // 
  // const {sendMsg,             teamData:taskDone,      onMyMount,                      onMyCleanup}                        = useWebSocketComm('mark-team-task-done', null,'', 'team-task-done',     false);

  // TODO: Replace with new hooks
  const {sendMsg:updateTask,  teamData:taskUpdated,   onMyMount:onMountForUpdateTask, onMyCleanup:onCleanupForUpdateTask} = useWebSocketComm('update-team-task',    null,'', 'team-task-updated',  false);  

  //
  //---[  EFFECT  ]---  []
  //
  // useEffect(()=>{
  //   onMyMount();
  //   onMountForUpdateTask();    
  //   return ()=>{
  //     onMyCleanup();
  //     onCleanupForUpdateTask();      
  //   }
  // }, [onCleanupForUpdateTask, onMountForUpdateTask, onMyCleanup, onMyMount]);

  const cubeSize = useMemo(()=> Math.max(Math.min(640 / tikiTasks.length, 120), 72), [tikiTasks.length]);
  //
  // ---[  EFFECT  ]---   [ tasks ]
  //
  useEffect(()=>{
    const getViewLimit = () => {
      const totalH = window.innerHeight;
      const totalW = window.innerWidth;      
      const optimalRows =  Math.floor( (totalH * 0.7) / (cubeSize * 1.5) * 0.5 );
      const optimalCols =  Math.floor( (totalW * 0.6) / (cubeSize * 1.5) * 0.5 );
      const limit = optimalCols * optimalRows;
      console.log('[Optimal R:%s C:%s]', optimalRows, optimalCols);      
      return limit;
    }

    const calculateMaxDisplay = () => {
      setMaxDisplay( getViewLimit() );
    };

    window.addEventListener('resize', calculateMaxDisplay);

    if (Boolean(tasks) && Boolean(cubeSize) && Boolean(stages)) {
      totalTasksRef.current = tasks.length;
      const sorted = sortByStage(tasks);
      setTikiTasks(sorted);
      if (sorted && sorted?.length > 0) {

        const aboveStage = sorted?.filter(task =>  {
          const taskStage = task.stageId ? stages[task.stageId] : stages.todoLater;
          return taskStage.p > stages.todoLater.p;
        }).length;

        const viewLimit = getViewLimit();
        const upToLimit = Math.max( Math.min( viewLimit, aboveStage ), 3);
        
        setMaxDisplay( upToLimit ); // Only Active Tasks...
      }
    }

    return () => {
      window.removeEventListener('resize', calculateMaxDisplay);
    }
  }, [tasks, cubeSize, sortByStage]);
  

  //
  // ---[  EFFECT  ]--- [taskUpdated]
  //
  useEffect(()=>{
    if (taskUpdated) {
      console.log('Task Updated');      
      whenTaskUpdated(taskUpdated);
    }
  }, [taskUpdated, whenTaskUpdated]);
  

  // Cube Tags
  const cubeBack = (task) => {
    const miniTagsComp = task.tags?.map(tag => <MiniTag title={tag} key={tag}/>);
    return <div className='cubeTags'>
      <div>Currently editing: {task.𝓡?.e}</div>
      {miniTagsComp}
    </div>    
  }

  // ---[  SEND Mark Task as Done  ]---
  //   
  const markDone = useCallback((task) => {
    console.log('markDone: %s', JSON.stringify(task));    
    sendMessage({
      taskId: task.taskId,
      owner: operator.username,
      teamId: operator.selectedTeamId, 
      accountId: operator.accountId,
      notes: ''
    });
  }, [sendMessage, operator?.username, operator?.selectedTeamId, operator?.accountId]);

  // Cube FRONT
  const cubeFront = (t) => {
    return <div className='iqFront'>
              <div className='iqFrontTitle'>{t.title}</div>
              <div className='iqFrontBody'>{t.body}</div>
          </div>
  }

  const {displayTimestamp} = useDisplayTime();

  // Cube TOP
  const cubeTop = (task) => {
    const dueTxt = task.whenDue > 0 ? `Due: ${displayTimestamp(task.whenDue)}` : '';
    const doneButton = <div className='iqDoneButton' onClick={(e)=> {
      e.stopPropagation();
      markDone(task);
    }}>
      Mark Done!
    </div>

    return <div className='iqTop'>
      <div>
        {dueTxt}
      </div>
      <div className='iqTopDone'>
        {doneButton}
      </div>
    </div>
  }

  const cubeLeft = (task) => {
    const stageId = task.stageId ? task.stageId : sortedStageKeys[1]; // Mark as "todoLater"
    return  <div className='iqLeft'>
      <ProgPanel stageId={stageId} task={task}/>
    </div>
  }

  const cubeRight = (task) => {
    return `Est: ${task.whenDue}`;
  }

  const onCubeTakeOut = (task) => {
    whenTaskUpdated({...task, '𝓡' : {e: operator.username}});
  }

  const onCubeReturn = useCallback((task) => {    
    updateTask({
      taskId: task.taskId,
      fieldName: 'RE',
      fieldValue: null,
      teamId: operator.selectedTeamId,
      accountId: operator.accountId  
    });    
  },[updateTask, operator?.selectedTeamId, operator?.accountId]);

  //    ---[  RENDER  ]---
  

  // ALL BOXES
  const allCubes = tikiTasks.slice(0,maxDisplay)
        .filter(task => !task.𝓡?.e)
        .map(task => 
        <InfoCube   wallSize={cubeSize}
                    wallWidth={cubeSize}
                    key={task.taskId}
                    front={cubeFront(task)} 
                    back={cubeBack(task)} 
                    top={cubeTop(task)}
                    bottom={task.taskId} 
                    left={cubeLeft(task)} 
                    right={cubeRight(task)}
                    isEdit={false}
                    onEdit={() => onCubeTakeOut(task)}
                />);

  const editCubes = tikiTasks.filter(task => task.𝓡?.e)
        .map(task => 
        <InfoCube   wallSize={cubeSize}
                    wallWidth={cubeSize}
                    key={task.taskId}
                    front={cubeFront(task)} 
                    back={cubeBack(task)} 
                    top={cubeTop(task)}
                    bottom={task.taskId} 
                    left={cubeLeft(task)} 
                    right={cubeRight(task)}
                    onEdit={() => onCubeReturn(task)}
                    isEdit={true}
                />);      

  return (
    
    <div className='tikia'>
      
      <TikiaContext.Provider value={{        
        cubeReset,        
        keyPressed,
        stages,
        sortedStageKeys,
        updateTask,
        }}>
          <div className='tikiHeader'>
            {tikiName}({maxDisplay} of {tikiTasks?.length})
          </div>
          <div className='tikiWork'>
            <div className='tikiTasks'>
              {allCubes}
            </div>
            <div className='editArea'>
              {editCubes}
            </div>
          </div>
          <div className='tikiTags'>
          </div>

      </TikiaContext.Provider>

    </div>
  )
}